import { DestroyRef, inject, Injectable, LOCALE_ID } from '@angular/core';
import { map, Observable, shareReplay, Subject, tap } from 'rxjs';
import { DeleteIssueRequestDTO } from '../interface/audit-trail.interface';
import {
  ExistingIssueContext,
  ExistingIssueRequestDTO,
  ExportExcelPayloadDTO,
  IssueInfoResponse,
  IssueCategoryDTO,
  NewIssueRequestDTO,
  UpdateFilterIssueRequestDTO,
} from '../interface/all-issues.interface';
import { ApiService } from './app-api.service';
import { AppCommonService } from './app-common.service';
import { SessionStorageService } from './app-session.service';
import { ToastService } from './app-toast.service';
import { ApiResponseDTO } from '../interface/app-API.interface';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterLookupResponse } from '../constants/issue-filter.model.constants';
import { EXPORT_ERROR_MESSAGES } from '../constants/export-excel.constants';
import {
  ExportScreen,
  FilterTabActiveFilter,
  YearFilterType,
  TaxAccountingActiveFilter,
  TaxAccountingIssueType,
} from '../enums/export-excel.enums';
import {
  ExportExcelCommand,
  ExportExcelRequest,
  FilterTabExportValues,
  TaxAccountingExportValues,
} from '../models/export-excel.models';
import {
  After2017RecordDTO,
  IssueInfoRecordDTO,
  IssuePrior2018RecordDTO,
} from '../interface/issues-2017-2018.interface';

interface ActiveFilter<TFilter> {
  readonly type: TFilter;
}

type CancelIssueRecord = IssuePrior2018RecordDTO | After2017RecordDTO | IssueInfoRecordDTO;

@Injectable({
  providedIn: 'root',
})
export class AppIssuesService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly apiService = inject(ApiService);
  private readonly commonService = inject(AppCommonService);
  private readonly toastService = inject(ToastService);
  private readonly sessionService = inject(SessionStorageService);
  private readonly fb = inject(FormBuilder);
  private readonly locale = inject(LOCALE_ID);
  existingIssueForm!: FormGroup;
  private filters$?: Observable<ApiResponseDTO<FilterLookupResponse>>;

  private refreshIssueDetails = new Subject<string>();
  reloadIssueDetail$ = this.refreshIssueDetails.asObservable();

  private readonly navigateToAllIssuesSubject = new Subject<void>();
  navigateToAllIssues$ = this.navigateToAllIssuesSubject.asObservable();

  private navigateToAllIssues(): void {
    this.navigateToAllIssuesSubject.next();
  }

  /** REFRESH_DATA:  */
  triggerRefresh(state: string): void {
    this.refreshIssueDetails.next(state);
  }

  constructor() {
    this.createExistingIssueForm();
  }

  createNewIssueForm(): FormGroup {
    return this.fb.group({
      idIssueCategory: ['', Validators.required],
      issueID: ['', Validators.required],
      description: ['', Validators.required],
    });
  }

  createExistingIssueForm() {
    this.existingIssueForm = this.fb.group(
      {
        id: [0],
        issueId: [''],
        description: [''],
        entityCode: ['', Validators.required],
        issueYear: [null, [Validators.required, Validators.pattern(/^\d{4}$/)]],
        epAdjRequired: [''],
        contact: [''],
        approver: [''],
        tbbsAdjRequired: [''],
        cfcNc902: [''],
        dateReviewed: [''],
        dateCorptaxAdj: [''],
        adjDescrip: [''],
        arFilingId: [''],
        arIssueId: [''],
        irsIssueID: [''],
        saa: [''],
        submission: [''],
        spDocLink: [''],
        npa: [''],
        npaTiesToArSaa: [''],
        issueStatus: ['Active'],
        deduction199: false,
        creationDate: Date,
        modificationDate: Date,
      },
      {
        validators: [this.saaOrArFilingValidator()],
      },
    );
  }

  private saaOrArFilingValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const saa = control.get('saa')?.value;
      const arFilingId = control.get('arFilingId')?.value;

      const hasSaa = !!saa;
      const hasArFilingId = !!arFilingId;

      if (!hasSaa && !hasArFilingId) {
        return { filingRequired: true };
      }

      if (hasSaa && hasArFilingId) {
        return { onlyOneAllowed: true };
      }

      return null;
    };
  }

  resetNewIssueForm(form: FormGroup): void {
    form.reset({
      category: '',
      issueID: '',
      description: '',
    });

    form.markAsPristine();
    form.markAsUntouched();

    Object.keys(form.controls).forEach((key) => {
      form.get(key)?.setErrors(null);
    });
  }

  private existingIssueContext?: ExistingIssueContext;

  setExistingIssueContext(context: ExistingIssueContext): void {
    this.existingIssueContext = context;
  }

  getExistingIssueContext(): ExistingIssueContext | undefined {
    return this.existingIssueContext;
  }

  clearExistingIssueContext(): void {
    this.existingIssueContext = undefined;
  }

  loadCategories(): Observable<IssueCategoryDTO[]> {
    return this.apiService.getCategoriesList().pipe(map((response) => response.result ?? []));
  }

  generateIssueId(categoryId: number): Observable<string> {
    return this.apiService.generateIssueID().pipe(
      map((response) => {
        const issueId = response.result ?? '';
        const formattedIssueId = issueId.toString().length === 3 ? `0${issueId}` : issueId;

        return `${categoryId}-${formattedIssueId}`;
      }),
    );
  }

  saveOrUpdateIssue(payload: NewIssueRequestDTO): Observable<ApiResponseDTO<string>> {
    return this.apiService.saveUpdateNewIssue(payload);
  }

  saveOrUpdateExistingIssued(payload: ExistingIssueRequestDTO, issueId: string): void {
    this.apiService
      .saveUpdateExistingIssue(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const message = `${response.message} for Issue ID: ${issueId}.`;
          this.toastService.success(message);
          this.clearFiltersCache();
          this.navigateToAllIssues();
        },
        error: (error) => {
          console.error('Save existing issue error:', error);
          this.toastService.error('Unable to save the issue.');
        },
      });
  }

  updateIssueFilterDetails(
    payload: UpdateFilterIssueRequestDTO,
    issueId: string,
    isEditCase: boolean = false,
  ): void {
    this.apiService
      .updateIssueFilterDetails(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const message = `Issue details updated successfully for Issue ID: ${issueId}.`;
          this.toastService.success(message);
          this.clearFiltersCache();
          if (isEditCase) {
            this.triggerRefresh('refresh-edit');
          }
        },
        error: (error) => {
          console.error('UpdateIssueFilterDetails API error:', error);
        },
      });
  }

  cancelIssue(payload: DeleteIssueRequestDTO): Observable<ApiResponseDTO<unknown>> {
    return this.apiService.cancelIssueAdj(payload).pipe(
      tap(() => {
        const msg = `Issue ${payload.irsIssueID} cancelled successfully.`;
        this.toastService.success(msg);
      }),
    );
  }

  createCancelIssuePayload(
    selectedRow: CancelIssueRecord,
    issueInfos: readonly IssueInfoResponse[] | null | undefined,
  ): DeleteIssueRequestDTO {
    const currentDate = this.commonService.createISOTimeZone(new Date().toString());
    const currentUser = JSON.parse(sessionStorage.getItem('currentUser') ?? '{}') as {
      readonly emailId?: string;
    };
    const userName = currentUser.emailId ?? '';
    const issueInfo = issueInfos?.find((record) => record.id === selectedRow.id);
    const oldIrsIssueId = issueInfo?.irsIssueID ?? '';

    return {
      id: selectedRow.id ?? 0,
      irsIssueID: 'Cancel',
      dateModification: currentDate,
      auditTrail: [
        {
          auditTrailID: 0,
          auditDateTime: currentDate,
          userName,
          formName: 'filtersub_IssueEffect',
          recordID: String(selectedRow.id),
          fieldName: 'irsIssueID',
          oldValue: oldIrsIssueId,
          newValue: 'Cancel',
        },
      ],
    };
  }

  formatAmount(value: number | null | undefined): string {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(Number(value ?? 0));
  }

  getFiltersCached(): Observable<ApiResponseDTO<FilterLookupResponse>> {
    if (!this.filters$) {
      this.filters$ = this.apiService.getFilters().pipe(shareReplay(1));
    }
    return this.filters$;
  }

  clearFiltersCache(): void {
    this.filters$ = undefined;
  }

  buildRequest(command: ExportExcelCommand): ExportExcelRequest {
    switch (command.screen) {
      case ExportScreen.FilterTab:
        return this.buildFilterTabRequest(command.values);

      case ExportScreen.TaxAccounting:
        return this.buildTaxAccountingRequest(command.values);

      default:
        throw new Error('Unsupported filter type');
    }
  }

  private buildFilterTabRequest(values: FilterTabExportValues): ExportExcelRequest {
    const activeFilter = this.detectFilterTabActiveFilter(values);

    switch (activeFilter.type) {
      case FilterTabActiveFilter.IssueId:
        return {
          issueID: this.requireText(values.issueId, EXPORT_ERROR_MESSAGES.noActiveFilter),
        };

      case FilterTabActiveFilter.FromDate:
        return {
          date: this.toIsoDate(values.fromDate),
        };

      case FilterTabActiveFilter.IssueStatus:
        return {
          issue_Status: this.requireText(values.issueStatus, EXPORT_ERROR_MESSAGES.noActiveFilter),
        };

      case FilterTabActiveFilter.ArFilingId:
        return {
          amendedID: this.requireText(values.arFilingId, EXPORT_ERROR_MESSAGES.noActiveFilter),
        };

      case FilterTabActiveFilter.ArIssueId:
        return {
          arIssueID: this.requireText(values.arIssueId, EXPORT_ERROR_MESSAGES.noActiveFilter),
        };

      case FilterTabActiveFilter.Year:
        return this.buildYearRequest(values);

      default:
        throw new Error('Unsupported filter type');
    }
  }

  private buildYearRequest(values: FilterTabExportValues): ExportExcelRequest {
    const issueYear = this.requireYear(values.year);
    const yearFilterType = values.yearFilterType;

    if (yearFilterType === null) {
      throw new Error(EXPORT_ERROR_MESSAGES.missingYearFilterType);
    }

    switch (yearFilterType) {
      case YearFilterType.AllIssue:
        return {
          issue_Year: issueYear,
          allIssueFlag: true,
        };

      case YearFilterType.AmendedReturn:
        return {
          issue_Year: issueYear,
          amended_ReturnFlag: true,
        };

      case YearFilterType.Saa:
        return {
          issue_Year: issueYear,
          saA_ID: this.requireText(values.saa, EXPORT_ERROR_MESSAGES.missingSaa),
        };

      case YearFilterType.CcnLegal:
        return {
          issue_Year: issueYear,
          ccn: this.requireText(values.ccnLegal, EXPORT_ERROR_MESSAGES.missingCcnLegal),
        };

      default:
        throw new Error('Unsupported filter type');
    }
  }

  private buildTaxAccountingRequest(values: TaxAccountingExportValues): ExportExcelRequest {
    const activeFilter = this.detectTaxAccountingActiveFilter(values);

    const issueTypeFlag = this.buildTaxAccountingIssueTypeFlag(values.issueType);

    switch (activeFilter.type) {
      case TaxAccountingActiveFilter.IssueId:
        return {
          issueID: this.requireText(values.issueId, EXPORT_ERROR_MESSAGES.noActiveFilter),
          ...issueTypeFlag,
        };

      case TaxAccountingActiveFilter.Tbbs:
        return {
          tbbsStatus: this.requireText(values.tbbs, EXPORT_ERROR_MESSAGES.noActiveFilter),
          ...issueTypeFlag,
        };

      case TaxAccountingActiveFilter.IssueStatus:
        return {
          issue_Status: this.requireText(values.issueStatus, EXPORT_ERROR_MESSAGES.noActiveFilter),
          ...issueTypeFlag,
        };

      case TaxAccountingActiveFilter.IssueDate:
        return {
          date: this.toIsoDate(values.issueDate),
          ...issueTypeFlag,
        };

      default:
        throw new Error('Unsupported filter type');
    }
  }

  private detectFilterTabActiveFilter(
    values: FilterTabExportValues,
  ): ActiveFilter<FilterTabActiveFilter> {
    const activeFilters: FilterTabActiveFilter[] = [];

    if (this.hasText(values.issueId)) {
      activeFilters.push(FilterTabActiveFilter.IssueId);
    }

    if (this.hasDateValue(values.fromDate)) {
      activeFilters.push(FilterTabActiveFilter.FromDate);
    }

    if (this.hasText(values.issueStatus)) {
      activeFilters.push(FilterTabActiveFilter.IssueStatus);
    }

    if (this.hasText(values.arFilingId)) {
      activeFilters.push(FilterTabActiveFilter.ArFilingId);
    }

    if (this.hasText(values.arIssueId)) {
      activeFilters.push(FilterTabActiveFilter.ArIssueId);
    }

    if (values.year !== null) {
      activeFilters.push(FilterTabActiveFilter.Year);
    }

    return {
      type: this.requireExactlyOneActiveFilter(activeFilters),
    };
  }

  private detectTaxAccountingActiveFilter(
    values: TaxAccountingExportValues,
  ): ActiveFilter<TaxAccountingActiveFilter> {
    const activeFilters: TaxAccountingActiveFilter[] = [];

    if (this.hasText(values.issueId)) {
      activeFilters.push(TaxAccountingActiveFilter.IssueId);
    }

    if (this.hasText(values.tbbs)) {
      activeFilters.push(TaxAccountingActiveFilter.Tbbs);
    }

    if (this.hasText(values.issueStatus)) {
      activeFilters.push(TaxAccountingActiveFilter.IssueStatus);
    }

    if (this.hasDateValue(values.issueDate)) {
      activeFilters.push(TaxAccountingActiveFilter.IssueDate);
    }

    return {
      type: this.requireExactlyOneActiveFilter(activeFilters),
    };
  }

  private buildTaxAccountingIssueTypeFlag(
    issueType: TaxAccountingIssueType | string | null,
  ): Pick<ExportExcelRequest, 'allIssueFlag' | 'asC_740Flag'> {
    const normalizedIssueType = issueType?.trim();

    switch (normalizedIssueType) {
      case TaxAccountingIssueType.AllIssues:
        return {
          allIssueFlag: true,
        };

      case TaxAccountingIssueType.Asc74010:
        return {
          asC_740Flag: true,
        };

      default:
        throw new Error(EXPORT_ERROR_MESSAGES.invalidTaxAccountingIssueType);
    }
  }

  private requireExactlyOneActiveFilter<TFilter>(activeFilters: readonly TFilter[]): TFilter {
    if (activeFilters.length === 0) {
      throw new Error(EXPORT_ERROR_MESSAGES.noActiveFilter);
    }

    if (activeFilters.length > 1) {
      throw new Error(EXPORT_ERROR_MESSAGES.multipleActiveFilters);
    }

    return activeFilters[0];
  }

  private requireText(value: string | null, errorMessage: string): string {
    const normalizedValue = value?.trim() ?? '';

    if (normalizedValue.length === 0) {
      throw new Error(errorMessage);
    }

    return normalizedValue;
  }

  private requireYear(value: number | null): number {
    if (value === null || !Number.isInteger(value) || value <= 0) {
      throw new Error(EXPORT_ERROR_MESSAGES.missingYearFilterType);
    }

    return value;
  }

  private toIsoDate(value: Date | string | null): string {
    if (!this.hasDateValue(value)) {
      throw new Error(EXPORT_ERROR_MESSAGES.invalidDate);
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
      throw new Error(EXPORT_ERROR_MESSAGES.invalidDate);
    }

    return date.toISOString();
  }

  private hasText(value: string | null): boolean {
    return (value?.trim().length ?? 0) > 0;
  }

  private hasDateValue(value: Date | string | null): value is Date | string {
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }

    return typeof value === 'string' && value.trim().length > 0;
  }

  private assertNever(value: never): never {
    throw new Error(`Unsupported export configuration: ${String(value)}`);
  }

  createEmptyExportPayload(): ExportExcelPayloadDTO {
    return {
      issueID: null,
      tbbsStatus: null,
      date: null,
      allIssueFlag: null,
      asC_740Flag: null,
      issue_Status: null,
      amendedID: null,
      arIssueID: null,
      issue_Year: null,
      amended_ReturnFlag: null,
      saA_IDFlag: null,
      saA_ID: null,
      ccn: null,
    };
  }

  loadFillingIDs(id: number) {
    return this.apiService.getARFillingById(id);
  }
}
